import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { API_URL, popupTimer } from "../../consts";
import axios from "axios";
import { useDispatch } from "react-redux";

const PublicVisibilityDialog = ({ open, onClose, ratingId, currentIsPublic, onUpdate }) => {
  const dispatch = useDispatch();
  const [isPublic, setIsPublic] = useState(currentIsPublic || false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggle = (event) => {
    event.stopPropagation();
    setIsPublic(event.target.checked);
  };

  const handleSave = async (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await axios.put(`${API_URL}/rates/ratings/${ratingId}`, {
        is_public: isPublic,
      });

      dispatch({
        type: "SET_POPUP",
        payload: {
          text: isPublic ? "Rating is now public" : "Rating is now private",
          type: "success",
        },
      });

      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);

      if (onUpdate) {
        onUpdate(isPublic);
      }

      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err?.response?.data?.error || "Failed to update visibility. Please try again.",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setIsPublic(currentIsPublic || false); // Reset to original value
      onClose();
    }
  };

  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      onClick={handleDialogClick}
      onMouseDown={handleDialogClick}
      PaperProps={{
        onClick: handleDialogClick,
        onMouseDown: handleDialogClick,
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Change Visibility
        </Typography>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          disabled={isUpdating}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }} onClick={handleDialogClick} onMouseDown={handleDialogClick}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            onClick={handleDialogClick}
            onMouseDown={handleDialogClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: isPublic
                ? "rgba(25, 118, 210, 0.08)"
                : "rgba(0, 0, 0, 0.04)",
              border: `1px solid ${
                isPublic ? "rgba(25, 118, 210, 0.2)" : "rgba(0, 0, 0, 0.1)"
              }`,
            }}
          >
            {isPublic ? (
              <VisibilityIcon sx={{ fontSize: 32, color: "primary.main" }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 32, color: "text.secondary" }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {isPublic ? "Public" : "Private"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isPublic
                  ? "This rating is visible to everyone in the community"
                  : "This rating is only visible to you"}
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            onClick={handleDialogClick}
            onMouseDown={handleDialogClick}
            control={
              <Switch
                checked={isPublic}
                onChange={handleToggle}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isUpdating}
                color="primary"
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Make this rating {isPublic ? "private" : "public"}
              </Typography>
            }
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        onClick={handleDialogClick}
        onMouseDown={handleDialogClick}
        sx={{
          px: 3,
          pb: 2,
          pt: 2,
          borderTop: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          disabled={isUpdating}
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleSave();
          }}
          disabled={isUpdating || isPublic === (currentIsPublic || false)}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublicVisibilityDialog;
